import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import Div from "components/primitives/Div";

interface ValidationAlertsProps {
  missingAthletes: Array<{ name: string; athleteId: string }>;
  missingExercises: Array<{ exerciseName: string; columns: string[] }>;
  invalidValues: Array<{ athleteName: string; exerciseName: string; value: string }>;
}

function ValidationAlerts(props: ValidationAlertsProps) {
  const { missingAthletes, missingExercises, invalidValues } = props;
  const { t } = useTranslation();

  const hasMissingAthletes = useMemo(() => missingAthletes.length > 0, [missingAthletes.length]);
  const hasMissingExercises = useMemo(() => missingExercises.length > 0, [missingExercises.length]);
  const hasInvalidValues = useMemo(() => invalidValues.length > 0, [invalidValues.length]);

  const hasAnyValidationIssues = useMemo(
    () => hasMissingAthletes || hasMissingExercises || hasInvalidValues,
    [hasMissingAthletes, hasMissingExercises, hasInvalidValues]
  );

  return (
    <Div sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Collapse in={!hasAnyValidationIssues}>
        <Alert severity="success">
          <AlertTitle>{t("athlete.bulk_1rm_validation_success")}</AlertTitle>
          <Typography variant="body2">{t("athlete.bulk_1rm_validation_no_errors")}</Typography>
        </Alert>
      </Collapse>

      <Collapse in={hasMissingAthletes}>
        <Alert severity="warning">
          <AlertTitle>{t("athlete.bulk_1rm_missing_athletes_title")}</AlertTitle>
          <Typography variant="body2">
            {t("athlete.bulk_1rm_missing_athletes_description", { count: missingAthletes.length })}
          </Typography>
          <Div sx={{ mt: 1 }}>
            {missingAthletes.slice(0, 5).map((athlete, index) => (
              <Typography key={index} variant="body2" sx={{ fontFamily: "monospace" }}>
                • {athlete.name}
              </Typography>
            ))}
            {missingAthletes.length > 5 && (
              <Typography variant="body2" sx={{ fontStyle: "italic", mt: 0.5 }}>
                {t("global.and_more", { count: missingAthletes.length - 5 })}
              </Typography>
            )}
          </Div>
        </Alert>
      </Collapse>

      <Collapse in={hasMissingExercises}>
        <Alert severity="error">
          <AlertTitle>{t("athlete.bulk_1rm_missing_exercises_title")}</AlertTitle>
          <Typography variant="body2">
            {t("athlete.bulk_1rm_missing_exercises_description", { count: missingExercises.length })}
          </Typography>
          <Div sx={{ mt: 1 }}>
            {missingExercises.slice(0, 5).map((exercise, index) => (
              <Typography key={index} variant="body2" sx={{ fontFamily: "monospace" }}>
                • {exercise.exerciseName}
              </Typography>
            ))}
            {missingExercises.length > 5 && (
              <Typography variant="body2" sx={{ fontStyle: "italic", mt: 0.5 }}>
                {t("global.and_more", { count: missingExercises.length - 5 })}
              </Typography>
            )}
          </Div>
        </Alert>
      </Collapse>

      <Collapse in={hasInvalidValues}>
        <Alert severity="error">
          <AlertTitle>{t("athlete.bulk_1rm_invalid_values_title")}</AlertTitle>
          <Typography variant="body2">
            {t("athlete.bulk_1rm_invalid_values_description", { count: invalidValues.length })}
          </Typography>
          <Div sx={{ mt: 1 }}>
            {invalidValues.slice(0, 5).map((item, index) => {
              // eslint-disable-next-line i18next/no-literal-string
              const errorText = `• ${item.athleteName} - ${item.exerciseName}: "${item.value}"`;
              return (
                <Typography key={index} variant="body2" sx={{ fontFamily: "monospace" }}>
                  {errorText}
                </Typography>
              );
            })}
            {invalidValues.length > 5 && (
              <Typography variant="body2" sx={{ fontStyle: "italic", mt: 0.5 }}>
                {t("global.and_more", { count: invalidValues.length - 5 })}
              </Typography>
            )}
          </Div>
        </Alert>
      </Collapse>
    </Div>
  );
}

export default ValidationAlerts;
